import { useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  Church,
  Clock3,
  MapPin,
  Users,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import type {
  ServiceDayOfWeek,
  ServiceCreationRequest,
  ServiceType,
} from "@/types/service";

type Props = {
  contextName?: string;
  creationContext?: string;
  timezone?: string;
  open: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (
    request: ServiceCreationRequest
  ) => Promise<void>;
};

type FormState = {
  isActive: boolean;
  name: string;
  serviceType: ServiceType;
  dayOfWeek: ServiceDayOfWeek;
  firstDate: string;
  startTime: string;
  endTime: string;
  location: string;
  capacity: string;
};

const initialForm: FormState = {
  isActive: true,
  name: "",
  serviceType: "WORSHIP",
  dayOfWeek: "SUNDAY",
  firstDate: "",
  startTime: "10:00",
  endTime: "12:00",
  location: "",
  capacity: "",
};

export function ScheduleServiceDialog(props: Props) {
  return props.open ? <ScheduleServiceForm {...props} /> : null;
}

function ScheduleServiceForm({
  contextName,
  creationContext,
  timezone,
  isSubmitting,
  errorMessage,
  onClose,
  onSubmit,
}: Props) {
  const [frequency, setFrequency] = useState<"ONE_TIME" | "RECURRING">("ONE_TIME");
  const submitting = useRef(false);
  const creationIntent = useRef<{ signature: string; key: string } | null>(null);
  const recurring = frequency === "RECURRING";
  const [form, setForm] = useState<FormState>(initialForm);
  const [validationError, setValidationError] = useState<
    string | null
  >(null);

  const selectedDateDay = useMemo(() => {
    if (!form.firstDate) {
      return null;
    }

    const date = new Date(`${form.firstDate}T12:00:00`);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date
      .toLocaleDateString("en-US", {
        weekday: "long",
      })
      .toUpperCase();
  }, [form.firstDate]);

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    if (submitting.current || isSubmitting) return;
    setValidationError(null);

    if (!form.name.trim()) {
      setValidationError("Service name is required.");
      return;
    }

    if (!form.firstDate) {
      setValidationError(recurring ? "First service date is required." : "Service date is required.");
      return;
    }

    if (!form.startTime) {
      setValidationError("Start time is required.");
      return;
    }

    if (
      recurring && selectedDateDay &&
      selectedDateDay !== form.dayOfWeek
    ) {
      setValidationError(
        `The selected first date falls on ${formatLabel(
          selectedDateDay
        )}, but the recurring schedule is set to ${formatLabel(
          form.dayOfWeek
        )}.`
      );

      return;
    }

    if (!recurring && !form.endTime) {
      setValidationError("End time is required.");
      return;
    }

    if (
      form.endTime &&
      form.endTime <= form.startTime
    ) {
      setValidationError(
        "End time must be later than start time."
      );

      return;
    }

    const capacity = form.capacity
      ? Number(form.capacity)
      : null;

    if (
      capacity !== null &&
      (!Number.isInteger(capacity) || capacity < 1)
    ) {
      setValidationError(
        "Capacity must be a whole number greater than zero."
      );

      return;
    }

    const common = { name: form.name.trim(), service_type: form.serviceType, location: form.location.trim() || null, capacity };
    const request: ServiceCreationRequest = recurring
      ? { frequency: "RECURRING", schedule: { ...common, starts_on: form.firstDate, day_of_week: form.dayOfWeek, start_time: form.startTime, end_time: form.endTime || null, is_active: form.isActive } }
      : { frequency: "ONE_TIME", occurrence: { ...common, starts_at: new Date(`${form.firstDate}T${form.startTime}:00`).toISOString(), ends_at: new Date(`${form.firstDate}T${form.endTime}:00`).toISOString() } };
    // Preserve the intent across lost-response retries; changed input is a new intent.
    const signature = JSON.stringify({ request, creationContext, contextName, timezone });
    if (creationIntent.current?.signature !== signature) {
      creationIntent.current = { signature, key: crypto.randomUUID() };
    }
    if (request.frequency === "RECURRING") request.schedule.creation_key = creationIntent.current.key;
    else request.occurrence.creation_key = creationIntent.current.key;
    submitting.current = true;
    try {
      await onSubmit(request);
    } catch {
      // The parent mutation displays the server error and keeps the form open.
    } finally {
      submitting.current = false;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/80 px-4 py-8 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-700 bg-slate-900 shadow-2xl shadow-black/50">
        <div className="relative border-b border-slate-800 px-7 py-6">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 via-indigo-500 to-emerald-400" />

          <div className="flex items-start justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-500/15 text-violet-400">
                <CalendarDays size={22} />
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-white">
                  Create service
                </h2>

                <p className="mt-1 text-sm text-slate-400">
                  {recurring ? "Repeats weekly while active. MinistryIQ maintains the next eight weeks automatically, using your church timezone." : "Create one service on the selected date."}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-white disabled:opacity-50"
              aria-label="Close schedule service dialog"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 p-7 md:grid-cols-2">
            <fieldset className="md:col-span-2" disabled={isSubmitting}>
              <legend className="mb-2 text-sm font-medium text-slate-300">Service frequency</legend>
              <div className="flex flex-wrap gap-3">
                <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-slate-700 px-3 text-sm text-slate-200 transition hover:bg-slate-800 has-checked:border-indigo-400 has-checked:bg-indigo-500/15 has-checked:text-white has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-indigo-400 has-disabled:cursor-not-allowed has-disabled:opacity-60"><input className="h-4 w-4 accent-indigo-500" type="radio" name="service-frequency" value="ONE_TIME" checked={!recurring} onChange={() => { setFrequency("ONE_TIME"); setValidationError(null); }} /> One-time</label>
                <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-slate-700 px-3 text-sm text-slate-200 transition hover:bg-slate-800 has-checked:border-indigo-400 has-checked:bg-indigo-500/15 has-checked:text-white has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-indigo-400 has-disabled:cursor-not-allowed has-disabled:opacity-60"><input className="h-4 w-4 accent-indigo-500" type="radio" name="service-frequency" value="RECURRING" checked={recurring} onChange={() => { setFrequency("RECURRING"); setValidationError(null); }} /> Recurring</label>
              </div>
            </fieldset>
            <p className="md:col-span-2 text-sm text-slate-300">{contextName ?? "Selected service context"} · Times in {recurring ? timezone ?? "your church timezone" : Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
            {recurring && <label className="md:col-span-2 text-sm text-slate-300"><input type="checkbox" checked={form.isActive} onChange={event => setForm(current => ({ ...current, isActive: event.target.checked }))} /> Active weekly schedule</label>}
            <FormField
              label="Service Name"
              icon={<Church size={16} />}
              className="md:col-span-2"
            >
              <input
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="Sunday Morning Worship"
                className={inputClassName}
                autoFocus
              />
            </FormField>

            <FormField
              label="Service Type"
              icon={<Church size={16} />}
            >
              <select
                value={form.serviceType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    serviceType:
                      event.target.value as ServiceType,
                  }))
                }
                className={inputClassName}
              >
                <option value="WORSHIP">
                  Sunday Service
                </option>
                <option value="BIBLE_STUDY">
                  Bible Study
                </option>
                <option value="PRAYER">
                  Prayer
                </option>
                <option value="YOUTH">
                  Youth
                </option>
                <option value="SPECIAL">
                  Special Event
                </option>
              </select>
            </FormField>

            {recurring && <FormField
              label="Repeats Every"
              icon={<CalendarDays size={16} />}
            >
              <select
                value={form.dayOfWeek}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    dayOfWeek:
                      event.target
                        .value as ServiceDayOfWeek,
                  }))
                }
                className={inputClassName}
              >
                {DAYS.map((day) => (
                  <option key={day} value={day}>
                    {formatLabel(day)}
                  </option>
                ))}
              </select>
            </FormField>}

            <FormField
              label={recurring ? "First Service Date" : "Service Date"}
              icon={<CalendarDays size={16} />}
            >
              <input
                type="date"
                value={form.firstDate}
                onChange={(event) => {
                  const value = event.target.value;

                  setForm((current) => ({
                    ...current,
                    firstDate: value,
                    dayOfWeek:
                      getDayOfWeek(value) ??
                      current.dayOfWeek,
                  }));
                }}
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="Location"
              icon={<MapPin size={16} />}
            >
              <input
                value={form.location}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    location: event.target.value,
                  }))
                }
                placeholder="Main Sanctuary"
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="Start Time"
              icon={<Clock3 size={16} />}
            >
              <input
                type="time"
                value={form.startTime}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    startTime: event.target.value,
                  }))
                }
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="End Time"
              icon={<Clock3 size={16} />}
            >
              <input
                type="time"
                value={form.endTime}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    endTime: event.target.value,
                  }))
                }
                className={inputClassName}
              />
            </FormField>

            <FormField
              label="Capacity"
              icon={<Users size={16} />}
              className="md:col-span-2"
            >
              <input
                type="number"
                min="1"
                step="1"
                value={form.capacity}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    capacity: event.target.value,
                  }))
                }
                placeholder="Optional"
                className={inputClassName}
              />
            </FormField>

            {(validationError || errorMessage) && (
              <div className="md:col-span-2 rounded-2xl border border-rose-500/20 bg-rose-500/5 px-5 py-4 text-sm text-rose-300">
                {validationError || errorMessage}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-slate-800 bg-slate-950/30 px-7 py-5 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Scheduling..."
                : "Create service"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({
  label,
  icon,
  className,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={className}>
      <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
        <span className="text-slate-500">
          {icon}
        </span>

        {label}
      </span>

      <span className="mt-2 block">{children}</span>
    </label>
  );
}

const inputClassName =
  "h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white placeholder:text-slate-600 focus:border-violet-500 focus:outline-none";

const DAYS: ServiceDayOfWeek[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

function getDayOfWeek(
  value: string
): ServiceDayOfWeek | null {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T12:00:00`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date
    .toLocaleDateString("en-US", {
      weekday: "long",
    })
    .toUpperCase() as ServiceDayOfWeek;
}

function formatLabel(value: string) {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}